import React, { useEffect, useState } from "react";
import axios from "axios";
import { SealCheck, CaretUp, CaretDown, CaretRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASS_LABELS = { steers: "Steers", heifers: "Heifers", young_bulls: "Young Bulls", cows: "Cows" };

const Change = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-[#9CA3AF] text-xs">—</span>;
  const up = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono text-xs ${up ? "text-[#059669]" : value < 0 ? "text-[#DC2626]" : "text-[#4B5563]"}`}>
      {up ? <CaretUp size={11} weight="fill" /> : value < 0 ? <CaretDown size={11} weight="fill" /> : null}
      {up ? "+" : ""}{value.toFixed(1)}p
    </span>
  );
};

export const AHDBOfficialPrices = () => {
  const [data, setData] = useState(null);
  const [sheep, setSheep] = useState(null);
  const [pigs, setPigs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegions, setShowRegions] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API}/uk/ahdb/deadweight`, { timeout: 60000 }),
      axios.get(`${API}/uk/ahdb/sheep`, { timeout: 60000 }),
      axios.get(`${API}/uk/ahdb/pigs`, { timeout: 60000 }),
    ]).then(([cattle, sh, pg]) => {
      if (cattle.status === "fulfilled") setData(cattle.value.data);
      if (sh.status === "fulfilled") setSheep(sh.value.data);
      if (pg.status === "fulfilled") setPigs(pg.value.data);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <div className="bg-white border border-[#E0D4C8] p-6 mb-6 animate-pulse" data-testid="ahdb-loading"><div className="h-5 bg-[#E5E7EB] w-1/3 mb-4"></div><div className="h-20 bg-[#F3F4F6]"></div></div>;
  }

  if (!data?.available) {
    return (
      <div className="bg-[#FEF3C7] border border-[#FCD34D] px-4 py-3 mb-6" data-testid="ahdb-unavailable">
        <p className="text-sm text-[#92400E]">Official AHDB feed temporarily unavailable — showing indicative boards below.</p>
      </div>
    );
  }

  const regions = (data.regions || []).filter((r) => r.region !== "Great Britain");
  const sheepBands = sheep?.available ? [
    ["sqq", "Lamb SQQ (overall)"],
    ["standard", "Standard"],
    ["medium", "Medium"],
  ].filter(([k]) => sheep.bands?.[k]) : [];

  return (
    <div className="bg-white border-2 border-[#1D4ED8] mb-6" data-testid="ahdb-official-prices">
      <div className="px-4 py-3 bg-[#1D4ED8] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <SealCheck size={20} className="text-[#93C5FD]" weight="fill" />
          <h2 className="font-serif text-lg text-white">Official AHDB Prices — GB Deadweight</h2>
          <span className="px-2 py-0.5 bg-[#93C5FD] text-[#1E3A8A] text-[10px] font-mono uppercase tracking-wide" data-testid="ahdb-official-tag">Official</span>
        </div>
        <span className="text-xs font-mono text-[#BFDBFE]">wk {data.week}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[#E0D4C8]">
        {Object.entries(CLASS_LABELS).map(([cls, label]) => {
          const c = data.gb?.[cls];
          if (!c) return null;
          return (
            <div key={cls} className="p-4" data-testid={`ahdb-${cls}`}>
              <p className="text-[10px] font-mono uppercase text-[#6B6661] mb-1">{label}</p>
              <p className="text-2xl font-mono text-[#111827]">{c.average_price?.toFixed(1)}p</p>
              <Change value={c.price_change} />
              <p className="text-[10px] font-mono text-[#6B6661] mt-1">
                {c.number?.toLocaleString()} head{c.reference_price ? ` · ${c.reference_grade}: ${c.reference_price.toFixed(1)}p` : ""}
              </p>
            </div>
          );
        })}
      </div>

      {sheepBands.length > 0 && (
        <div data-testid="ahdb-sheep-section">
          <div className="px-4 py-1.5 bg-[#EFF6FF] border-t border-[#E0D4C8] flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-wider text-[#1D4ED8]">GB Deadweight Sheep — NSL weekly</span>
            <span className="text-[10px] font-mono text-[#6B6661]">wk {sheep.week}</span>
          </div>
          <div className="grid grid-cols-3 divide-x divide-[#E0D4C8]">
            {sheepBands.map(([key, label]) => {
              const b = sheep.bands[key];
              return (
                <div key={key} className="p-4" data-testid={`ahdb-sheep-${key}`}>
                  <p className="text-[10px] font-mono uppercase text-[#6B6661] mb-1">{label}</p>
                  <p className="text-2xl font-mono text-[#111827]">{b.average_price?.toFixed(1)}p</p>
                  <Change value={b.price_change} />
                  <p className="text-[10px] font-mono text-[#6B6661] mt-1">
                    {b.weight_range}{b.number ? ` · ${b.number.toLocaleString()} head` : ""}{b.reference_price ? ` · ${b.reference_grade}: ${b.reference_price.toFixed(1)}p` : ""}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {pigs?.available && (
        <div data-testid="ahdb-pig-section">
          <div className="px-4 py-1.5 bg-[#FDF2F8] border-t border-[#E0D4C8] flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-wider text-[#BE185D]">GB Standard Pig Price — SPP weekly</span>
            <span className="text-[10px] font-mono text-[#6B6661]">wk ending {pigs.week}</span>
          </div>
          <div className="grid grid-cols-3 divide-x divide-[#E0D4C8]">
            <div className="p-4" data-testid="ahdb-pig-spp">
              <p className="text-[10px] font-mono uppercase text-[#6B6661] mb-1">SPP (EU spec)</p>
              <p className="text-2xl font-mono text-[#111827]">{pigs.spp.price?.toFixed(2)}p</p>
              <Change value={pigs.spp.change} />
              <p className="text-[10px] font-mono text-[#6B6661] mt-1">
                {pigs.spp.throughput ? `${pigs.spp.throughput.toLocaleString()} pigs` : ""}
              </p>
            </div>
            <div className="p-4" data-testid="ahdb-pig-ukspec">
              <p className="text-[10px] font-mono uppercase text-[#6B6661] mb-1">UK Spec</p>
              <p className="text-2xl font-mono text-[#111827]">{pigs.uk_spec.price ? `${pigs.uk_spec.price.toFixed(2)}p` : "—"}</p>
              <Change value={pigs.uk_spec.change} />
            </div>
            <div className="p-4" data-testid="ahdb-pig-3yr">
              <p className="text-[10px] font-mono uppercase text-[#6B6661] mb-1">3-Year Average</p>
              <p className="text-2xl font-mono text-[#6B6661]">{pigs.spp.three_yr_avg ? `${pigs.spp.three_yr_avg.toFixed(2)}p` : "—"}</p>
              <p className="text-[10px] font-mono text-[#6B6661] mt-1">same week, 3yr mean</p>
            </div>
          </div>
        </div>
      )}

      <button onClick={() => setShowRegions(!showRegions)} data-testid="ahdb-regions-toggle"
        className="w-full px-4 py-2 flex items-center gap-1 text-xs font-mono text-[#1D4ED8] hover:bg-[#EFF6FF] border-t border-[#E0D4C8] transition-colors">
        <CaretRight size={12} className={`transition-transform ${showRegions ? "rotate-90" : ""}`} />
        Regional breakdown
      </button>
      {showRegions && (
        <div className="overflow-x-auto border-t border-[#E0D4C8]" data-testid="ahdb-regions-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F6E9DD]">
              <tr>
                <th className="text-left px-4 py-2 text-[10px] font-mono uppercase text-[#6B6661]">Region</th>
                {Object.values(CLASS_LABELS).map((l) => (
                  <th key={l} className="text-right px-4 py-2 text-[10px] font-mono uppercase text-[#6B6661]">{l}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {regions.map((r) => (
                <tr key={r.region} className="hover:bg-[#F9FAFB]">
                  <td className="px-4 py-2 text-[#33302E]">{r.region}</td>
                  {Object.keys(CLASS_LABELS).map((cls) => (
                    <td key={cls} className="px-4 py-2 text-right font-mono text-[#33302E]">
                      {r.classes?.[cls]?.average_price ? `${r.classes[cls].average_price.toFixed(1)}p` : "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-4 py-2 bg-[#F9FAFB] border-t border-[#E0D4C8]">
        <p className="text-[11px] text-[#6B6661]">
          Source: <a href={data.source_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#1D4ED8]">AHDB GB deadweight cattle</a>
          {sheep?.available && <> & <a href={sheep.source_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#1D4ED8]">sheep</a></>} surveys — official reported prices, {data.unit}. © Agriculture and Horticulture Development Board.
        </p>
      </div>
    </div>
  );
};

export default AHDBOfficialPrices;
